import { ReactElement } from 'react';
import { useMutation } from '@apollo/client/react';
import { useForm } from '@tanstack/react-form';
import { Input, Modal, ModalFooter } from '@sc/shared-ui';
import Globe from '../assets/icons/globe-01.svg';
import {
  GQL_MUTATION_CREATE_DOMAIN,
  GQL_QUERY_INDEX_DOMAINS,
} from '../utility/queries';
import { notify } from '../utility/notify';
import { useAlerts } from '../providers/alerts';
import { VerifiableDomain } from './DomainVerificationModal';
import { track } from '../utility/analytics';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated?: ((domain: VerifiableDomain) => void) | undefined;
}

export const CreateDomainModal = ({
  open,
  onClose,
  onCreated,
}: Props): ReactElement => {
  const [createDomain, { loading }] = useMutation(GQL_MUTATION_CREATE_DOMAIN);

  const { useApolloError } = useAlerts();

  const onError = useApolloError();

  const form = useForm({
    defaultValues: {
      url: '',
    },
    onSubmit: (form) =>
      void createDomain({
        variables: {
          input: {
            url: form.value.url,
          },
        },
        refetchQueries: [GQL_QUERY_INDEX_DOMAINS],
        onCompleted: (data) => {
          form.formApi.reset();

          notify.success('Successfully created domain');

          track('Domain Created', { domainUuid: data.createDomain.uuid });

          onClose();

          onCreated?.({
            uuid: data.createDomain.uuid,
            url: data.createDomain.url,
          });
        },
        onError,
      }),
  });

  const close = () => {
    onClose();

    void form.reset();
  };

  return (
    <Modal
      title="Add domain"
      loading={loading}
      open={open}
      onClose={close}
      dialogPanelClassname="w-[440px]"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();

          e.stopPropagation();

          void form.handleSubmit();
        }}
      >
        <div className="p-5">
          <form.Field
            name="url"
            validators={{
              onChange: (state) =>
                /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?){2,}$/.test(
                  state.value
                )
                  ? undefined
                  : `Please ensure you are only entering a subdomain with no http or https://`,
            }}
            children={({ state, handleChange }) => (
              <Input
                label="Web domain"
                helperText="Once added here, you will be able to utilise this domain for your knowledge bases."
                value={state.value}
                Icon={Globe}
                placeholder="e.g. mysite.com"
                required
                error={state.meta.errors.join(', ')}
                onChange={(e) => handleChange(e.target.value)}
              />
            )}
          />
        </div>
        <ModalFooter asForm onClose={close} submitText="Save & add" />
      </form>
    </Modal>
  );
};
